import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatINR, useAuth, todayIst } from '../lib/store';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, Package, FileText, Tags, Clock, ArrowRight, Layers, Palette, TreePine, LayoutGrid, Wrench, Pipette, DoorOpen, Box, Sparkles, PanelTop, Loader2, Plus, Zap, Users, Phone, AlertCircle } from 'lucide-react';
import WalkinFormDialog from '../components/forms/WalkinFormDialog';
import LeadFormDialog from '../components/forms/LeadFormDialog';
import ComplaintDialog from '../components/ComplaintDialog';
import { logger } from '../lib/logger';

const CATEGORY_ICON_MAP = {
  'Plywood': Layers, 'Laminates': Palette, 'Highlighters': Sparkles, 'Veneers': TreePine,
  'Wooden Flooring': LayoutGrid, 'Hardware': Wrench, 'ACP': PanelTop, 'Adhesives': Pipette,
  'Doors': DoorOpen, 'WPC/PVC': Box, 'MDF': Package,
};

// iter-119b: `todayIstIso` used to live here with the buggy
// `getTimezoneOffset` math. Now sourced from the shared `todayIst`
// helper in lib/store.js — device-timezone-independent, always the
// current IST calendar date.
const todayIstIso = todayIst;

// "Welcome, Anand": the first name, or the email's name part when there is no name.
const greetingName = (user) => {
  const s = (user?.name || '').trim().split(/\s+/)[0] || user?.email?.split('@')[0] || 'User';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_products: 0, total_quotations: 0, total_brands: 0, recent_quotations: [] });
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);

  const [walkinOpen, setWalkinOpen] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [complaintOpen, setComplaintOpen] = useState(false);
  // Red-dot indicator on the "My Registrations" card when the current
  // user has 1+ overdue follow-ups. Reuses TodayFollowupsBanner's exact
  // data call — no new endpoint. Silent on failure so a hiccup never
  // blocks the dashboard.
  const [overdueCount, setOverdueCount] = useState(0);

  // "My Pending Quotes" card badge. Reuses the existing
  // /quotations/stale-pending-summary endpoint with the new `mine=1`
  // flag (returns owner-scoped counts even for admins). No new
  // endpoint; two numbers so the card can go red only when 1+ quotes
  // are 30+ days old but still show a neutral count otherwise. Silent
  // on failure so a hiccup never blocks the dashboard.
  const [pendingQuotesCount, setPendingQuotesCount] = useState(0);
  const [stalePendingCount, setStalePendingCount] = useState(0);

  useEffect(() => {
    if (user?.role === 'admin') return undefined;    // admin card is hidden
    const today = todayIstIso();
    let cancelled = false;
    // Quotations have their own tracking (Quotations page +
    // WhatsApp digest) so the My Registrations red-dot now only counts
    // walk-ins + leads — matches what the /my-registrations page
    // actually shows after quotations were removed from it.
    Promise.all([
      api.listMyWalkins({ follow_up_date_to: today, limit: 200 }),
      api.listMyLeads({ follow_up_date_to: today, limit: 200 }),
    ])
      .then(([wr, lr]) => {
        if (cancelled) return;
        const ws = (wr.data?.walkins || [])
          .filter((w) => !w.follow_up_reminder_sent || w.status !== 'closed');
        const ls = (lr.data?.leads || [])
          .filter((l) => l.status !== 'converted' && l.status !== 'lost');
        // A missing follow_up_date is NOT overdue — only a date strictly
        // before today counts. (Keeps the dashboard dot honest and in
        // sync with the /my-registrations Overdue box.)
        const overdue = [...ws, ...ls].filter((it) => it.follow_up_date && it.follow_up_date < today).length;
        setOverdueCount(overdue);
      })
      .catch(() => { /* silent — never block the dashboard */ });
    return () => { cancelled = true; };
  }, [user?.role]);

  useEffect(() => {
    api.getDashboardStats().then(r => setStats(r.data)).catch(logger.error);
    api.getCategories().then(r => setCategories(r.data)).catch(logger.error);
    // "My Pending Quotes" badge — owner-scoped for every role.
    // `mine=1` forces admin/manager/dispatcher to see only their own
    // pending count instead of the whole company's. Refires on every
    // dashboard mount so returning from the list picks up any newly
    // marked-closed / marked-lost quotes automatically.
    api.getStalePendingSummary({ mine: 1 })
      .then(r => {
        setPendingQuotesCount(r.data?.total_pending || 0);
        setStalePendingCount(r.data?.count || 0);
      })
      .catch(() => { /* silent — never block the dashboard */ });
  }, []);

  // Live search with debounce. State-setter calls are wrapped in
  // `queueMicrotask` so they run *after* the effect returns — required
  // by the `react-hooks/set-state-in-effect` rule on React 19.
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      queueMicrotask(() => {
        setSearchResults([]);
        setShowResults(false);
      });
      return undefined;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.getProducts({ search: searchQuery.trim(), limit: 8 });
        setSearchResults(res.data.products || []);
        setShowResults(true);
      } catch (err) {
        logger.error(err);
      }
      setSearching(false);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowResults(false);
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="overflow-x-hidden" data-testid="dashboard-page">
      {/* Welcome (tagline removed for a cleaner top-of-page). */}
      <div className="mb-8">
        <h1 className="font-display text-2xl md:text-[30px] font-bold text-heda-ink mb-1">
          Welcome, {greetingName(user)}
        </h1>
      </div>

      {/* Live product search — no separate SEARCH button. Enter still
          navigates to the full /products search as a fallback. */}
      <form onSubmit={handleSearch} className="mb-8 animate-fade-in" ref={searchRef}>
        <div className="relative max-w-2xl">
          {searching ? (
            <Loader2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-heda-gold animate-spin" />
          ) : (
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-heda-mute" />
          )}
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchResults.length > 0 && setShowResults(true)}
            onKeyDown={(e) => e.key === 'Escape' && setShowResults(false)}
            placeholder=""
            className="rounded-xl pl-12 pr-4 h-14 text-base bg-white border-heda-line focus:border-heda-gold focus:ring-heda-gold/20 shadow-sm"
            data-testid="dashboard-search-input"
          />
          
          {/* Live Search Results Dropdown */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-heda-line rounded-xl shadow-lg z-50 max-h-96 overflow-y-auto">
              {searchResults.map((product, idx) => (
                <button
                  key={product.id || idx}
                  type="button"
                  className="w-full px-4 py-3 text-left hover:bg-heda-cream/60 border-b border-heda-line last:border-b-0 flex items-center justify-between gap-3"
                  onClick={() => {
                    setShowResults(false);
                    // Navigate using the unique product id so the Products
                    // page can show ONLY this exact product card (avoids
                    // sub-code searches failing to find the parent product).
                    if (product.id) {
                      navigate(`/products?productId=${encodeURIComponent(product.id)}`);
                    } else {
                      const searchTerm = product.product_code || `${product.brand_name} ${product.subcategory || product.product_name || ''}`;
                      navigate(`/products?search=${encodeURIComponent(searchTerm.trim())}`);
                    }
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-heda-ink truncate">
                      {product.brand_name} {product.subcategory || product.product_name}
                    </div>
                    <div className="text-xs text-heda-mute flex gap-2">
                      <span>{product.main_category}</span>
                      {product.thickness && <span>• {product.thickness}</span>}
                      {product.codes_and_rates?.length > 1 && <span>• {product.codes_and_rates.length} codes</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-mono text-sm font-semibold text-heda-olive-dark">
                      {formatINR(product.base_rate)}
                    </div>
                    <div className="text-xs text-heda-mute">{product.rate_type}</div>
                  </div>
                </button>
              ))}
              <button
                type="button"
                className="w-full px-4 py-2 text-center text-sm text-heda-olive-dark hover:bg-heda-cream/60 font-medium"
                onClick={() => {
                  setShowResults(false);
                  navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
                }}
              >
                View all results →
              </button>
            </div>
          )}
          
          {/* No results message with Quick Add options */}
          {showResults && searchQuery.trim().length >= 2 && searchResults.length === 0 && !searching && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-heda-line rounded-xl shadow-lg z-50 p-4">
              <div className="text-center text-sm text-heda-mute mb-3">
                No products found for &quot;{searchQuery}&quot;
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                <span className="text-xs text-heda-sub self-center">Quick Add:</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-xl h-7 text-xs bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  onClick={() => {
                    setShowResults(false);
                    navigate(`/products?quickadd=laminate&code=${encodeURIComponent(searchQuery)}`);
                  }}
                >
                  <Palette className="w-3 h-3 mr-1" /> Laminate
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-xl h-7 text-xs bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  onClick={() => {
                    setShowResults(false);
                    navigate(`/products?quickadd=highlighter&code=${encodeURIComponent(searchQuery)}`);
                  }}
                >
                  <Sparkles className="w-3 h-3 mr-1" /> Highlighter
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-xl h-7 text-xs bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  onClick={() => {
                    setShowResults(false);
                    navigate(`/products?quickadd=hardware&code=${encodeURIComponent(searchQuery)}`);
                  }}
                >
                  <Wrench className="w-3 h-3 mr-1" /> Hardware
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-xl h-7 text-xs bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  onClick={() => {
                    setShowResults(false);
                    navigate(`/products?quickadd=adhesive&code=${encodeURIComponent(searchQuery)}`);
                  }}
                >
                  <Pipette className="w-3 h-3 mr-1" /> Adhesive
                </Button>
              </div>
            </div>
          )}
        </div>
      </form>

      {/* Action cards — primary entry points: Quotation, Walk-in, Lead,
          My Registrations, My Pending Quotes. Icon + title + arrow only
          — descriptions removed for a cleaner grid. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-8">
        <Card
          className="rounded-2xl bg-white shadow-none border-heda-line cursor-pointer hover:border-heda-gold hover:shadow-heda transition-all duration-200 group relative overflow-hidden"
          onClick={() => navigate('/products')}
          data-testid="home-card-quotation"
        >
          <CardContent className="flex items-center gap-3 py-5 px-4">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform bg-heda-olive-tint">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-heda-olive-dark" />
            </div>
            <p className="text-sm sm:text-base font-bold text-heda-ink leading-tight flex-1 min-w-0">Quotation</p>
            <ArrowRight className="w-4 h-4 flex-shrink-0 text-heda-mute group-hover:text-heda-olive-dark group-hover:translate-x-0.5 transition-all" />
          </CardContent>
        </Card>

        <Card
          className="rounded-2xl bg-white shadow-none border-heda-line cursor-pointer hover:border-heda-gold hover:shadow-heda transition-all duration-200 group relative overflow-hidden"
          onClick={() => setWalkinOpen(true)}
          data-testid="home-card-walkin"
        >
          <CardContent className="flex items-center gap-3 py-5 px-4">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform bg-heda-gold-tint">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-heda-gold" />
            </div>
            <p className="text-sm sm:text-base font-bold text-heda-ink leading-tight flex-1 min-w-0">Walk-in Register</p>
            <Plus className="w-4 h-4 flex-shrink-0 text-heda-mute group-hover:text-heda-gold transition-colors" />
          </CardContent>
        </Card>

        <Card
          className="rounded-2xl bg-white shadow-none border-heda-line cursor-pointer hover:border-heda-gold hover:shadow-heda transition-all duration-200 group relative overflow-hidden"
          onClick={() => setLeadOpen(true)}
          data-testid="home-card-lead"
        >
          <CardContent className="flex items-center gap-3 py-5 px-4">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform bg-sky-50">
              <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-sky-700" />
            </div>
            <p className="text-sm sm:text-base font-bold text-heda-ink leading-tight flex-1 min-w-0">Lead Register</p>
            <Plus className="w-4 h-4 flex-shrink-0 text-heda-mute group-hover:text-sky-700 transition-colors" />
          </CardContent>
        </Card>

        {/* My Registrations — hidden for admins. Red-dot indicator sits
            on the top-right when the current user has 1+ overdue
            follow-ups (walk-ins or leads). Never blocks the dashboard
            if the fetch fails. */}
        {user?.role !== 'admin' && (
        <Card
          className="rounded-2xl bg-white shadow-none border-heda-line cursor-pointer hover:border-heda-gold hover:shadow-heda transition-all duration-200 group relative overflow-hidden"
          onClick={() => navigate('/my-registrations')}
          data-testid="home-card-my-registrations"
        >
          {overdueCount > 0 && (
            <span
              className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-red-700 ring-2 ring-white"
              data-testid="my-registrations-overdue-dot"
              aria-label={`${overdueCount} overdue follow-up${overdueCount === 1 ? '' : 's'}`}
              title={`${overdueCount} overdue follow-up${overdueCount === 1 ? '' : 's'}`}
            />
          )}
          <CardContent className="flex items-center gap-3 py-5 px-4">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform bg-heda-cream">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-heda-sub" />
            </div>
            <p className="text-sm sm:text-base font-bold text-heda-ink leading-tight flex-1 min-w-0">My Registrations</p>
            <ArrowRight className="w-4 h-4 flex-shrink-0 text-heda-mute group-hover:text-heda-sub group-hover:translate-x-0.5 transition-all" />
          </CardContent>
        </Card>
        )}

        {/* My Pending Quotes — visible to every role that
            creates quotations (i.e. everyone; admins own quotes too,
            per Analytics.by_salesperson). The count badge turns red
            when 1+ of the user's own pending quotes are 30+ days old
            ("N need updating"); otherwise it's a neutral olive chip.
            Reuses stale-pending-summary?mine=1 — no new endpoint. */}
        <Card
          className="rounded-2xl bg-white shadow-none border-heda-line cursor-pointer hover:border-heda-gold hover:shadow-heda transition-all duration-200 group relative overflow-hidden"
          onClick={() => navigate('/my-pending-quotes')}
          data-testid="home-card-my-pending-quotes"
        >
          <CardContent className="flex items-center gap-3 py-5 px-4">
            <div
              className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform ${stalePendingCount > 0 ? 'bg-red-50' : 'bg-amber-50'}`}
            >
              <Clock className={`w-5 h-5 sm:w-6 sm:h-6 ${stalePendingCount > 0 ? 'text-red-700' : 'text-amber-700'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm sm:text-base font-bold text-heda-ink leading-tight">My Pending Quotes</p>
              {stalePendingCount > 0 ? (
                <p className="text-[11px] sm:text-xs text-red-700 mt-0.5 font-semibold" data-testid="my-pending-stale-line">
                  {stalePendingCount} need updating
                </p>
              ) : pendingQuotesCount > 0 ? (
                <p className="text-[11px] sm:text-xs text-heda-mute mt-0.5">Oldest first</p>
              ) : null}
            </div>
            <span
              className={`inline-flex items-center justify-center flex-shrink-0 min-w-[28px] h-7 px-2 rounded-full text-xs font-bold ring-2 ring-white ${
                stalePendingCount > 0
                  ? 'bg-red-700 text-white'
                  : 'bg-heda-olive-tint text-heda-olive-dark'
              }`}
              data-testid="my-pending-count-badge"
              aria-label={`${pendingQuotesCount} pending quote${pendingQuotesCount === 1 ? '' : 's'}${stalePendingCount > 0 ? `, ${stalePendingCount} over 30 days` : ''}`}
            >
              {pendingQuotesCount}
            </span>
          </CardContent>
        </Card>

        {/* Complaints — every logged-in role. Opens the ComplaintDialog;
            "My complaints" opens the list of the ones this user logged. */}
        <Card
          className="rounded-2xl bg-white shadow-none border-heda-line cursor-pointer hover:border-heda-gold hover:shadow-heda transition-all duration-200 group relative overflow-hidden"
          onClick={() => setComplaintOpen(true)}
          data-testid="home-card-complaint"
        >
          <CardContent className="flex items-center gap-3 py-5 px-4">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform bg-red-50">
              <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-700" />
            </div>
            <p className="text-sm sm:text-base font-bold text-heda-ink leading-tight flex-1 min-w-0">Complaints</p>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); navigate('/my-complaints'); }}
              className="heda-pill shrink-0 whitespace-nowrap !px-2.5 !py-1 text-[11.5px]"
              data-testid="home-card-my-complaints"
            >
              My complaints <ArrowRight className="w-3 h-3" />
            </button>
            <Plus className="w-4 h-4 flex-shrink-0 text-heda-mute group-hover:text-red-700 transition-colors" />
          </CardContent>
        </Card>
      </div>

      <WalkinFormDialog open={walkinOpen} onOpenChange={setWalkinOpen} />
      <LeadFormDialog open={leadOpen} onOpenChange={setLeadOpen} />
      <ComplaintDialog open={complaintOpen} onOpenChange={setComplaintOpen} />

      {/* Categories Grid */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="flex items-center gap-2 font-display text-[17px] font-bold text-heda-ink"><Layers size={16} className="text-heda-gold" /> Product Categories</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/products')} className="rounded-xl text-heda-olive-dark text-xs uppercase tracking-wider" data-testid="view-all-products-btn">
            View All <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {(() => {
            // Custom category order
            const categoryOrder = [
              'Plywood', 'Laminates', 'Veneers', 'Highlighters', 'Hardware', 'Doors',
              'Blockboard', 'MDF', 'WPC/PVC', 'ACP', 'Wooden Flooring', 'Adhesives'
            ];
            const sortedCategories = [...categories].sort((a, b) => {
              const aIdx = categoryOrder.indexOf(a);
              const bIdx = categoryOrder.indexOf(b);
              return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
            });
            return sortedCategories.map((cat, idx) => {
              const Icon = CATEGORY_ICON_MAP[cat] || Package;
              return (
                <Card
                  key={cat}
                  className="rounded-2xl bg-white shadow-none border-heda-line cursor-pointer hover:border-heda-gold hover:shadow-heda transition-all duration-200 group"
                  onClick={() => navigate(`/products?category=${encodeURIComponent(cat)}`)}
                  data-testid={`category-${cat.toLowerCase().replace(/[\s/]+/g, '-')}`}
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <CardContent className="flex flex-col items-center py-4 px-2 text-center">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-2 bg-heda-olive-tint group-hover:scale-110 transition-transform">
                      <Icon className="w-6 h-6 text-heda-olive-dark" />
                    </div>
                    <span className="text-xs font-semibold text-heda-ink leading-tight">{cat}</span>
                  </CardContent>
                </Card>
              );
            });
          })()}
        </div>
      </div>

      {/* Recent Quotations section removed per merchant feedback —
          staff almost never reference it from the dashboard. The
          history page (/quotations) remains the canonical view. */}

      {/* Quick Action — stacks vertically on phones so neither button can
          clip off the right edge, and the buttons grow to full width for
          easier tapping. (iter89 mobile pass.) */}
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <Button
          onClick={() => navigate('/products')}
          className="rounded-xl bg-heda-olive hover:bg-heda-olive-dark text-white shadow-heda uppercase text-xs tracking-wider font-semibold h-11 px-6 w-full sm:w-auto"
          data-testid="new-quotation-btn"
        >
          <Search className="w-4 h-4 mr-2" /> Browse Products
        </Button>
        <Button
          onClick={() => navigate('/cart')}
          variant="outline"
          className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold uppercase text-xs tracking-wider font-semibold h-11 px-6 w-full sm:w-auto"
          data-testid="go-to-cart-btn"
        >
          <FileText className="w-4 h-4 mr-2" /> View Cart
        </Button>
      </div>
    </div>
  );
}
